// 위젯 B8 — 서비스가 잘되었는지 확인 (★7 모니터링 열람)
// 원천: 남양주시 「N-CARE 통합돌봄 플랫폼 구축 계획(안)」 · 11단계 모니터링
//
// ⚠️ 정식 MonitoringRecord(★7) 모델 배포 전이므로 Visit·VisitReport를 프록시로 사용.
// 최초 1개월 / 정기 3개월 스케줄 준수율은 MonitoringRecord 배포 시 실측으로 교체.
import * as React from "react";
import { MetricDrilldownModal } from "../../MetricDrilldownModal";

interface Props {
  data: {
    region: { name: string; code: string };
    generated_at?: string;
    today: { planned: number; completed: number; overdue: number };
    weekly: { planned: number; completed: number; completion_rate_pct: number | null };
    reporting: { past_week_completed: number; reports_written: number; report_rate_pct: number | null };
    note?: string;
  };
}

export function MonitoringPulse({ data }: Props) {
  const { today, weekly, reporting } = data;
  // 지표 클릭 → 그 지표를 이루는 방문 목록(카드와 같은 스코프).
  const [metric, setMetric] = React.useState<string | null>(null);
  const rateColor = (r: number | null) =>
    r == null ? "var(--ink-4)" : r >= 90 ? "var(--green-500)" : r >= 70 ? "var(--amber-500)" : "var(--rose-500)";

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div style={{ padding: "18px 22px 12px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="h3">서비스 품질 · 모니터링</div>
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            방문 이행률 · 보고서 작성률 · <span style={{ color: "var(--orange-600)", fontWeight: 600 }}>지연 방문 즉시 확인</span>
          </div>
        </div>
        {today.overdue > 0 && (
          <span role="button" tabIndex={0} className="badge badge-rose"
            title={`계획일이 지난 미완료 방문 ${today.overdue}건 — 클릭 시 목록`}
            onClick={() => setMetric("overdue")}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setMetric("overdue"); } }}
            style={{ cursor: "pointer" }}>
            지연 <b style={{ marginLeft: 4 }}>{today.overdue}</b>
          </span>
        )}
      </div>

      {/* 오늘 방문 라인 */}
      <div style={{ padding: "8px 22px 4px" }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: "var(--sky-600)", letterSpacing: 0.5, marginBottom: 8 }}>오늘</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <Metric label="예정" value={today.planned} color="sky"
            onOpen={today.planned > 0 ? () => setMetric("today_planned") : null} />
          <Metric label="완료" value={today.completed} color="green"
            onOpen={today.completed > 0 ? () => setMetric("today_completed") : null} />
          <Metric label="지연" value={today.overdue} color={today.overdue > 0 ? "rose" : "gray"}
            onOpen={today.overdue > 0 ? () => setMetric("overdue") : null} />
        </div>
      </div>

      {/* 지난 7일 이행률 (rolling window - 요일 무관 안정 지표) */}
      <div style={{ padding: "14px 22px 6px" }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: "var(--green-600)", letterSpacing: 0.5, marginBottom: 8 }}>지난 7일 이행률</div>
        <RateBar
          label={`완료 ${weekly.completed} / 예정 ${weekly.planned}`}
          rate={weekly.completion_rate_pct}
          color={rateColor(weekly.completion_rate_pct)}
          onOpen={weekly.planned > 0 ? () => setMetric("week_planned") : null}
          title="지난 7일 방문 전체 목록 보기"
        />
      </div>

      {/* 보고서 작성률 */}
      <div style={{ padding: "12px 22px 20px" }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: "var(--orange-600)", letterSpacing: 0.5, marginBottom: 8 }}>보고서 작성률 (지난 7일)</div>
        <RateBar
          label={`작성 ${reporting.reports_written} / 완료 방문 ${reporting.past_week_completed}`}
          rate={reporting.report_rate_pct}
          color={rateColor(reporting.report_rate_pct)}
          onOpen={reporting.past_week_completed > reporting.reports_written
                    ? () => setMetric("reports_missing") : null}
          title="보고서가 아직 없는 완료 방문만 보기"
        />
        {data.note && (
          <div className="muted" style={{ fontSize: 10, marginTop: 10, fontStyle: "italic" }}>{data.note}</div>
        )}
      </div>

      {metric && (
        <MetricDrilldownModal
          endpoint="/api/v1/dashboard/region/monitoring_pulse/items"
          params={{ metric }}
          regionCode={data.region?.code}
          columns={["어르신", "담당", "상태", "일시"]}
          withTime
          emptyText="해당하는 방문이 없습니다."
          onClose={() => setMetric(null)}
        />
      )}
    </div>
  );
}

function Metric({ label, value, color, onOpen }: { label: string; value: number; color: string; onOpen?: (() => void) | null }) {
  const clickable = typeof onOpen === "function";
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      title={clickable ? `${label} 방문 ${value}건 목록 보기` : undefined}
      onClick={clickable ? onOpen : undefined}
      onKeyDown={(e) => { if (clickable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen(); } }}
      style={{ padding: "10px 12px", background: "var(--bg-sub)", borderRadius: 10, borderLeft: `3px solid var(--${color}-500)`,
               cursor: clickable ? "pointer" : "default" }}>
      <div className="muted" style={{ fontSize: 10.5, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: `var(--${color}-600)`, fontVariantNumeric: "tabular-nums", marginTop: 2, lineHeight: 1 }}>
        {value.toLocaleString("ko-KR")}
      </div>
    </div>
  );
}

function RateBar({ label, rate, color, onOpen, title }: { label: string; rate: number | null; color: string; onOpen?: (() => void) | null; title?: string }) {
  const displayRate = rate == null ? "—" : `${rate}%`;
  const width = rate == null ? 0 : Math.min(100, Math.max(0, rate));
  const clickable = typeof onOpen === "function";
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      title={clickable ? title : undefined}
      onClick={clickable ? onOpen : undefined}
      onKeyDown={(e) => { if (clickable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen(); } }}
      style={{ cursor: clickable ? "pointer" : "default" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
        <span className="muted" style={{ fontSize: 11 }}>{label}</span>
        <span style={{ fontSize: 18, fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>{displayRate}</span>
      </div>
      <div style={{ height: 8, background: "var(--bg-sub)", borderRadius: 99, overflow: "hidden" }}>
        <div style={{ width: `${width}%`, height: "100%", background: color, borderRadius: 99, transition: "width .3s ease" }}/>
      </div>
    </div>
  );
}
